import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useActivityViewData } from "#app/hooks/issue-view/use-activity-view-data";
import { useNoteMutations } from "#app/hooks/issue-view/use-note-mutations";
import { useMarkNotesRead } from "#app/hooks/issue-view/use-mark-notes-read";
import { useAppData } from "#app/lib/data";
import type { ActivityContextValue } from "#app/components/issue-view/activity/activity-context";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";

const HIGHLIGHT_DURATION = 3000;

export interface UseActivityContextValueResult {
  value: ActivityContextValue;
  loading: boolean;
  hasActivities: boolean;
  /** Loaded note threads (replies nested) — consumed by the media tab to
   * aggregate note photos without a second notes query. */
  notes: NoteNode[];
}

export function useActivityContextValue(
  issueId: string,
  focusNoteId?: string | null,
  autoScrollReady: boolean = true,
): UseActivityContextValueResult {
  const activityData = useActivityViewData(issueId);
  const noteMutations = useNoteMutations(issueId);
  const { markNoteRead } = useMarkNotesRead();
  const { currentUser } = useAppData();

  const [focusActivityId, setFocusActivityId] = useState<string | null>(null);
  const [focusNoteIdState, setFocusNoteIdState] = useState<string | null>(
    focusNoteId ?? null,
  );
  const clearFocusActivity = useCallback(() => {
    setFocusActivityId(null);
    setFocusNoteIdState(null);
  }, []);

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const noteRefsMap = useRef<Map<string, any>>(new Map());
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs that always hold the latest pagination state. scrollToNote runs an
  // async loop that loads pages until the target note appears; it must read
  // the latest hasMoreActivities and call the latest loadMoreActivities each
  // iteration, otherwise it captures stale closures and re-fetches the same
  // page forever.
  const hasMoreRef = useRef(activityData.hasMoreActivities);
  hasMoreRef.current = activityData.hasMoreActivities;
  const loadMoreRef = useRef(activityData.loadMoreActivities);
  loadMoreRef.current = activityData.loadMoreActivities;

  const registerNoteRef = useCallback((noteId: string, el: any) => {
    if (el) noteRefsMap.current.set(noteId, el);
    else noteRefsMap.current.delete(noteId);
  }, []);

  const scrollToNote = useCallback(
    async (noteId: string, parentNoteId?: string | null) => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);

      setSelectedNoteId(noteId);
      if (parentNoteId) setSelectedParentId(parentNoteId);

      const tryScroll = () => {
        const el = noteRefsMap.current.get(noteId);
        if (el) {
          el.scrollIntoView?.({ behavior: "smooth", block: "center" });
          highlightTimer.current = setTimeout(() => {
            setSelectedNoteId(null);
            setSelectedParentId(null);
          }, HIGHLIGHT_DURATION);
          return true;
        }
        return false;
      };

      if (tryScroll()) return;

      for (let i = 0; i < 10; i++) {
        await new Promise((r) => requestAnimationFrame(r));
        if (tryScroll()) return;
      }

      while (hasMoreRef.current) {
        await loadMoreRef.current();
        for (let i = 0; i < 3; i++) {
          await new Promise((r) => requestAnimationFrame(r));
          if (tryScroll()) return;
        }
      }

      await new Promise((r) => requestAnimationFrame(r));
      tryScroll();
    },
    [],
  );

  // Destructured so the effect depends on the two things it reads rather than
  // on the whole activity bag, which changes on every fetch.
  const { activities, getNote } = activityData;

  const didAutoScroll = useRef(false);
  useEffect(() => {
    if (
      focusNoteId &&
      autoScrollReady &&
      !didAutoScroll.current &&
      activities.length > 0
    ) {
      didAutoScroll.current = true;
      const note = getNote(focusNoteId);
      scrollToNote(focusNoteId, note?.parentNoteId);
    }
  }, [focusNoteId, autoScrollReady, activities.length, getNote, scrollToNote]);

  const value = useMemo<ActivityContextValue>(
    () => ({
      activities: activityData.activities,
      getNote: activityData.getNote,
      loadMoreActivities: activityData.loadMoreActivities,
      hasMoreActivities: activityData.hasMoreActivities,
      loadingMore: activityData.loadingMore,
      activityDates: activityData.activityDates,
      scrollToNote,
      selectedNoteId,
      selectedParentId,
      registerNoteRef,
      markNoteRead,
      createNote: noteMutations.createNote,
      updateNote: noteMutations.updateNote,
      deleteNote: noteMutations.deleteNote,
      toggleReaction: noteMutations.toggleReaction,
      currentUserId: currentUser?.userId ?? null,
      focusActivityId,
      focusNoteId: focusNoteIdState,
      clearFocusActivity,
    }),
    [
      activityData.activities, activityData.getNote, activityData.loadMoreActivities,
      activityData.hasMoreActivities, activityData.loadingMore, activityData.activityDates,
      scrollToNote,
      selectedNoteId, selectedParentId, registerNoteRef, markNoteRead,
      noteMutations.createNote, noteMutations.updateNote, noteMutations.deleteNote,
      noteMutations.toggleReaction, currentUser?.userId,
      focusActivityId, focusNoteIdState, clearFocusActivity,
    ],
  );

  return {
    value,
    loading: activityData.loading,
    hasActivities: activityData.activities.length > 0,
    notes: activityData.notes,
  };
}
